import { Key, useCallback } from 'react'

import {
	CardChoiceGroup,
	ChoiceDisplayType,
	RadioChoiceGroup,
	SelectChoiceGroup,
	ToggleChoiceGroup,
} from '../ChoiceGroup'
import { ChoiceOptionWithValue } from '../ChoiceGroup/ChoiceOption'

export type SimpleChoiceOptionWithValue = {
	value: string
	isDefaultSelected?: boolean
	label: string
	description?: string
	emoji?: string
}
type SimpleChoiceOptionWithChildren = {
	children: SimpleChoiceOptionWithValue[]
	label: string
	description?: string
}

export type SimpleChoiceOption =
	| SimpleChoiceOptionWithValue
	| SimpleChoiceOptionWithChildren

const isSimpleChoiceOptionWithValue = (
	choice: SimpleChoiceOption
): choice is SimpleChoiceOptionWithValue => Object.hasOwn(choice, 'value')

export interface ChoixUniqueProps {
	value?: string
	options: SimpleChoiceOption[]
	onChange: (value: string) => void
	id?: string
	title?: string
	description?: string
	autoFocus?: boolean
	variant?: ChoiceDisplayType
	defaultValue?: string
	aria?: {
		labelledby?: string
		describedby?: string
	}
}

/**
 * Composant de choix unique (boutons radio, cartes, etc.)
 * Version UI pure découplée de Publicodes
 */
export function ChoixUnique({
	value,
	defaultValue,
	options,
	onChange,
	title,
	autoFocus,
	variant = 'radio',
	aria,
}: ChoixUniqueProps) {
	const handleChange = useCallback(
		(val: Key) => {
			const stringVal = val.toString()
			if (!stringVal.length) {
				return
			}
			onChange(stringVal)
		},
		[onChange]
	)

	const getChoiceOptionWithValue = (
		option: SimpleChoiceOptionWithValue
	): ChoiceOptionWithValue => ({
		key: option.value,
		value: option.value,
		label: option.label,
		description: option.description,
		emoji: option.emoji,
		isDefaultSelected: option.isDefaultSelected || option.value === value,
	})

	const choiceOptions = options.map((option: SimpleChoiceOption) => {
		if (isSimpleChoiceOptionWithValue(option)) {
			return getChoiceOptionWithValue(option)
		}

		return {
			label: option.label,
			description: option.description,
			children: option.children.map((choice) =>
				getChoiceOptionWithValue(choice)
			),
		}
	})

	switch (variant) {
		case 'card':
			return (
				<CardChoiceGroup
					value={value}
					onChange={handleChange}
					options={choiceOptions}
					/* eslint-disable-next-line jsx-a11y/no-autofocus */
					autoFocus={autoFocus}
					defaultValue={defaultValue}
					title={title}
					aria={aria}
				/>
			)
		case 'toggle':
			return (
				<ToggleChoiceGroup
					value={value}
					onChange={handleChange}
					options={choiceOptions}
					/* eslint-disable-next-line jsx-a11y/no-autofocus */
					autoFocus={autoFocus}
					defaultValue={defaultValue}
					title={title}
					aria={aria}
				/>
			)
		case 'select':
			return (
				<SelectChoiceGroup
					value={value}
					onChange={handleChange}
					options={choiceOptions}
					/* eslint-disable-next-line jsx-a11y/no-autofocus */
					autoFocus={autoFocus}
					defaultValue={defaultValue}
					title={title}
					aria={aria}
				/>
			)
		case 'radio':
		default:
			return (
				<RadioChoiceGroup
					value={value}
					onChange={handleChange}
					options={choiceOptions}
					/* eslint-disable-next-line jsx-a11y/no-autofocus */
					autoFocus={autoFocus}
					defaultValue={defaultValue}
					aria={aria}
				/>
			)
	}
}
